import { Arbeitsschritt } from './types';
import type { ProjektMerkmale } from '../planungsphase/types';
import { findeDokument } from '../../services/mitgeltendeDokumente';

// Standard-Zeilen gemäß Muster-SiGePlan (VERA-II-Vorlage).
// Zeiträume bleiben leer — werden projektspezifisch gesetzt.
export type MusterZeile = Omit<Arbeitsschritt, 'id' | 'sige_projekt_id' | 'sortierung'> & {
  /**
   * Merkmale der Planungsphase, bei denen die Zeile gebraucht wird — nach dem
   * Muster `MitgeltendesDokument.merkmale`: die Zeile gilt, wenn EINES der
   * Merkmale zutrifft. Ohne Feld gilt sie unbedingt (jedes Vorhaben).
   * Ausgewertet wird dreiwertig in `musterVorauswahl.ts` (Entscheid E6):
   * ungeklärt bleibt vorgewählt, nur ein ausdrückliches Nein wählt ab.
   * Diese Angabe bleibt in der Vorlage — `baueMusterZeilen` schreibt sie nicht
   * in die Datenbank.
   */
  merkmale?: (keyof ProjektMerkmale)[];
};

const KOORD = 'alle Gewerke, Dritte';

/**
 * Name eines mitgeltenden Dokuments so, wie ihn der Katalog
 * (`services/mitgeltendeDokumente`) führt. Geprüft werden die Langform und
 * danach die Altschreibweisen der Vorlage; kennt der Katalog keine davon,
 * bleibt die Langform als freier Eintrag stehen. So passt die Vorlage auch
 * dann zum Katalog, wenn dieser einen Namen später anders schreibt.
 */
const dok = (langform: string, ...altschreibweisen: string[]): string => {
  for (const schluessel of [langform, ...altschreibweisen]) {
    const treffer = findeDokument(schluessel);
    if (treffer) return treffer.name;
  }
  return langform;
};

// Standen früher unter den Vorschriften — sind aber keine Rechtsnormen,
// sondern Unterlagen von Bauherr, Betreiber oder Hersteller.
const BAUSTELLENORDNUNG = dok('Baustellenordnung', 'BaustellenO');
const WERKSVORSCHRIFTEN = dok('Werksvorschriften des Betreibers', 'Werksvorschriften');
const LEITUNGSBETREIBER = dok('Vorgaben des Leitungsbetreibers', 'Vorschriften EVU', 'Vorschr. Stadtwerke');
const MONTAGEANWEISUNG = dok('Montageanweisung', 'Muster-MontageAnw');

/** Seltener gebrauchte Angaben einer Musterzeile — als Objekt, damit die Aufrufe lesbar bleiben. */
interface MusterZusatz {
  /** Gewerk der Zeile; leer lassen, die Übernahme setzt dann das Standard-Gewerk ein. */
  gewerk?: string;
  /**
   * Mitgeltende Dokumente, die zur Zeile gehören (Langform, siehe `dok`).
   * Weitere schlägt der Katalog bei der Übernahme aus Bezeichnung und Gewerk vor.
   */
  dokumente?: string[];
  /** Merkmale, bei denen die Zeile gebraucht wird (siehe `MusterZeile.merkmale`). */
  merkmale?: (keyof ProjektMerkmale)[];
}

const z = (
  hauptabschnitt: string,
  bezeichnung: string,
  schutzmassnahmen: string[],
  vorschriften: string[],
  hinweis_code: string,
  zustaendig_erstellung: string,
  zustaendig_kontrolle: string,
  zustaendig_instandhaltung: string,
  zusatz: MusterZusatz = {},
): MusterZeile => ({
  hauptabschnitt,
  bezeichnung,
  gewerk: zusatz.gewerk || '',
  schutzmassnahmen,
  vorschriften,
  mitgeltende_dokumente: [...(zusatz.dokumente || [])],
  hinweis_code,
  // "B 213; B 214" → ["B 213","B 214"]; "n.v." → keine Codes
  hinweis_codes: hinweis_code.split(';').map(s => s.trim()).filter(s => s && s.toLowerCase() !== 'n.v.'),
  koordinierungsbedarf: KOORD,
  zustaendig_erstellung,
  zustaendig_kontrolle,
  zustaendig_instandhaltung,
  // Nur setzen, wenn die Zeile ein Merkmal trägt — ohne Feld gilt sie unbedingt.
  ...(zusatz.merkmale?.length ? { merkmale: [...zusatz.merkmale] } : {}),
});

const VORBEREITUNG = 'Baustellenvorbereitung';
const EINRICHTUNG = 'Baustelleneinrichtung';
const VERKEHR = 'gemeinsam genutzte Einrichtung: VERKEHRSWEGE';
const LAGER = 'gemeinsam genutzte Einrichtung: LAGERPLÄTZE';
const ARBEITSPLAETZE = 'gemeinsam genutzte Einrichtung: ARBEITSPLÄTZE';
const UNTERKUENFTE = 'gemeinsam genutzte Einrichtung: UNTERKÜNFTE';
const MASCHINEN = 'gemeinsam genutzte Einrichtung: MASCHINEN, GERÄTE, GERÜSTE';
const ARBEITSSCHUTZ = 'gemeinsam genutzte Einrichtung: ARBEITSSCHUTZ';
const VERFAHREN = 'ARBEITSVERFAHREN mit Gefährdung anderer Gewerke';
const BESONDERS = 'Besondere Arbeitsbedingungen: Lärm, Hitze, Biostoffe';

export const MUSTER_ZEILEN: MusterZeile[] = [
  // ── Baustellenvorbereitung ─────────────────────────────────────────────
  z(VORBEREITUNG, 'Betriebsanlagen',
    ['Schriftliche Vereinbarung'],
    ['DGUV Vorschrift 1'],
    'n.v.', 'Bauherr', 'Bauherr', 'Bauherr',
    { dokumente: [BAUSTELLENORDNUNG, WERKSVORSCHRIFTEN], merkmale: ['betriebLaeuftWeiter'] }),
  z(VORBEREITUNG, 'erdverlegte Leitungen',
    ['Leitungen orten', 'Leitungen sichern', 'Leitungen umlegen', 'Bestands- / Leitungspläne'],
    ['DGUV Vorschrift 1', 'DGUV Vorschrift 3'],
    'C 472', 'Bauherr', 'Tiefbauer', 'jeweiliger Versorger',
    { dokumente: [LEITUNGSBETREIBER], merkmale: ['erdarbeiten'] }),
  z(VORBEREITUNG, 'Kampfmittel',
    ['Ermittlung der Kampfmittelverdachtsflächen', 'Kampfmittelbegleitdienst', 'Kampfmittelbeseitigungsdienst', 'Kampfmittelfreigabe'],
    ['GefStoffV', 'DGUV Regel 101-004', 'Sprengstoffgesetz', '2. SprengV, TRGS 524'],
    'n.v.', 'Bauherr', 'Tiefbauer', 'Kampfmittelräumdienst',
    { merkmale: ['erdarbeiten'] }),
  z(VORBEREITUNG, 'Nachbargebäude',
    ['Beweissicherung / Gipsmarken', 'Standsicherheitsnachweis', 'Unterfangungen', 'Verbau für Unterfangungen', 'Gebäudesicherung'],
    ['DIN 4123', 'DGUV Vorschrift 38'],
    'C 468', 'Tiefbauer', 'Tiefbauer', 'Tiefbauer'),

  // ── Baustelleneinrichtung ─────────────────────────────────────────────
  z(EINRICHTUNG, 'Allgemeinbeleuchtung in Gebäuden',
    ['Baustellenbeleuchtung', 'blendfrei', 'feuchtigkeitssicher'],
    ['ASR A 3.4', 'DGUV Vorschrift 1', 'DGUV Vorschrift 3', 'VDE', 'DIN 5035'],
    'B 171', 'Elektriker', 'alle Gewerke, Dritte', 'Elektrofachkraft'),
  z(EINRICHTUNG, 'Allgemeinbeleuchtung im Freien',
    ['Öffentliche Beleuchtung', 'Leuchten blendfrei', 'feuchtigkeitssicher'],
    ['ASR A 3.4, DIN 5035', 'DGUV Vorschrift 1', 'DGUV Vorschrift 3', 'VDE'],
    'A 024', 'Elektriker', 'alle Gewerke, Dritte', 'Elektrofachkraft'),
  z(EINRICHTUNG, 'Baugeräte',
    ['Standsicherheitsnachweis', 'CE-Zulassung', 'Prüfbuch', 'Zulassung nach GPSG'],
    ['TRA 1100', 'DGUV Vorschrift 52', 'BetrSichV', 'GPSG', 'DIN EN 418'],
    'B 211', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(EINRICHTUNG, 'Baustellensicherung',
    ['Bauzaun', 'Türen und Tore', 'Postenregelung'],
    ['LBO', 'DGUV Vorschrift 1'],
    'n.v.', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)'),
  z(EINRICHTUNG, 'Brandschutz',
    ['Handfeuerlöscher', 'Sachgerechte Aufstellung und Sicherung'],
    ['ASR A2.2', 'Ex-Schutz V'],
    'A 021', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)',
    { dokumente: [BAUSTELLENORDNUNG] }),
  z(EINRICHTUNG, 'Diesel-Tankanlagen',
    ['ASS (Abfüllschlauchsicherung) bei Befüllung'],
    ['WHG', 'BetrSichV', 'TRGS 554'],
    'A 068', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)'),
  z(EINRICHTUNG, 'Entsorgung',
    ['Abfallwirtschaft', 'Abwasser - Netz', 'Abwasser - Tank'],
    ['DGUV Vorschrift 1'],
    'n.v.', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)',
    { dokumente: [BAUSTELLENORDNUNG] }),
  z(EINRICHTUNG, 'Sozialeinrichtungen',
    ['Sanitäranlagen', 'Tagesunterkünfte'],
    ['ASR A4.1, ASR A4.3', 'ArbStättV', 'GewerbeOrdnung', 'DGUV Vorschrift 1'],
    'A 025', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)'),
  z(EINRICHTUNG, 'Versorgung',
    ['Strom - Aggregat', 'Strom - vom Netz', 'Wasser - vom Netz', 'Telefon'],
    ['BetrSichV', 'DGUV Vorschrift 79', 'DGUV Vorschrift 3', 'VDE 0200 - 0299', 'WHG', 'GPSG'],
    'n.v.', 'Bauherr', 'Elektrofachkraft', 'Elektrofachkraft'),

  // ── Verkehrswege ──────────────────────────────────────────────────────
  z(VERKEHR, 'Verkehrswege, allgemein: Stolpern, Rutschen, Stürzen',
    ['Freihalten', 'Ausreichend breit und rutschsicher', 'Tragfähigkeit', 'Beleuchtung'],
    ['DGUV Vorschrift 38', 'DGUV Vorschrift 1', 'ArbStättV', 'ASR A1.8'],
    'A 026', 'alle Gewerke', 'alle Gewerke', 'alle Gewerke'),
  z(VERKEHR, 'Verkehrswege im Freien: Fahrzeugverkehr',
    ['Rückwärtsfahrten vermeiden bzw. nur mit Einweiser', 'möglichst Einbahnstraßenverkehr', 'Trennung Verkehrsweg Kfz/Fußgänger'],
    ['DGUV Vorschrift 70', 'ArbStättV', 'ASR A1.8'],
    'A 026', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)'),
  z(VERKEHR, 'Verkehrswege am Baugrubenrand: Absturz, Einsturz',
    ['Böschungsneigung max. 45°', 'Seitenschutz: Bretter min. 3/15 cm'],
    ['DGUV Vorschrift 38', 'DIN 4124'],
    'A 026; C 470', 'Tiefbauer', 'Tiefbauer', 'Tiefbauer',
    { merkmale: ['erdarbeiten'] }),
  z(VERKEHR, 'Laufstege: Absturz, Stolpern',
    ['Trittleisten ab 1:5', 'Trittstufen ab 1:7,5'],
    ['DGUV Vorschrift 38', 'DGUV Vorschrift 1', 'ASR A1.8'],
    'A 026', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERKEHR, 'Leitern: Absturz',
    ['im Verkehrsbereich absperren', 'sichern gegen Umsturz, Einsinken', 'Leitern müssen die Austrittsstelle min. um 1,0 m überragen'],
    ['DGUV Vorschrift 38', 'TRBS 2121-2'],
    'B 131', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERKEHR, 'Gerüstaufstieg/-zugang: Absturz',
    ['3-teiliger Seitenschutz: Oberholm-Mittelh.-Bordbr.', 'Abstand zum Bauwerk max. 30 cm', 'Treppenturm', 'Innenliegender Leitergang'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811'],
    'B 113', 'Gerüstbauer', 'jeweiliges Gewerk', 'Gerüstbauer',
    { merkmale: ['geruest'] }),
  z(VERKEHR, 'Flucht und Rettungswege während der Bauphase',
    ['Flucht- und Rettungswegeplan', 'Einweisung in den Flucht- und Rettungswegeplan', 'Beschilderung der Flucht- und Rettungswege', 'Beleuchtung der Flucht- und Rettungswege'],
    ['ArbStättV', 'ASR A2.3', 'DGUV Vorschrift 1', 'ASR A1.8'],
    'A 026', 'Auftragnehmer (AN)', 'alle Gewerke', 'jeweiliges Gewerk'),
  z(VERKEHR, 'Arbeiten im öffentlichen Verkehrsraum: Anfahren durch Fahrzeuge',
    ['Verkehrssicherung nach RSA vor Arbeitsbeginn einrichten', 'Sicherheitsabstand zum fließenden Verkehr einhalten', 'Arbeitsstelle mit Absperrschranken / Leitbaken abgrenzen', 'Warnkleidung', 'Ein- und Ausfahren nur mit Einweiser', 'Verkehrssicherung täglich kontrollieren'],
    ['StVO § 45 (6)', 'RSA 21', 'ASR A5.2'],
    'n.v.', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)',
    { merkmale: ['strassenbaustelle'] }),
  z(VERKEHR, 'Arbeiten im Gleisbereich: Erfasstwerden durch Schienenfahrzeuge',
    ['Sicherungsmaßnahmen vorab mit dem Bahnbetreiber festlegen', 'Gleissperrung oder feste Absperrung vor Warnung', 'Sicherungsposten / Warnsystem bei Arbeiten unter Betrieb', 'Fahrleitung nur ausgeschaltet und geerdet', 'Warnkleidung', 'Unterweisung zum Verhalten im Gleisbereich'],
    ['DGUV Vorschrift 77/78'],
    'n.v.', 'Bauherr', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),

  // ── Lagerplätze ───────────────────────────────────────────────────────
  z(LAGER, 'Lagerplatz allgemein: Umsturz',
    ['Material/Baustoffe standsicher lagern', 'Lagerung außerhalb des Kranbereichs', 'Schutzbereich beachten'],
    ['DGUV Vorschrift 38', 'DGUV Vorschrift 1', 'DGUV Regel 100-001'],
    'n.v.', 'Bauherr', 'alle Gewerke, Dritte', 'jeweiliges Gewerk'),

  // ── Arbeitsplätze ─────────────────────────────────────────────────────
  z(ARBEITSPLAETZE, 'Baugruben-/Grabenkanten: Absturz, Böschungsbruch',
    ['Böschungsneigung max. 45°', 'Seitenschutz: Bretter min. 3/15 cm', 'Verbau - ab 1,25 m Tiefe'],
    ['DGUV Vorschrift 38', 'DIN 4124'],
    'A 026; C 469', 'Tiefbauer', 'Tiefbauer', 'Tiefbauer',
    { merkmale: ['erdarbeiten'] }),
  z(ARBEITSPLAETZE, 'Arbeiten an Deckenaußenkanten: Absturz',
    ['3-teil. Seitenschutz: Bretter min. 3/15 cm', 'Fanggerüst'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811'],
    'B 111', 'Rohbauer', 'Rohbauer', 'Rohbauer'),
  z(ARBEITSPLAETZE, 'Arbeiten an Deckeninnenkanten: Absturz',
    ['3-teil. Seitenschutz: Bretter min. 3/15 cm', 'Fanggerüst'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811'],
    'B 100', 'Rohbauer', 'Rohbauer', 'Rohbauer'),
  z(ARBEITSPLAETZE, 'Arbeiten an Dachkanten (Traufe/First): Absturz',
    ['3-teil. Seitenschutz: Bretter min. 3/15 cm', 'Dachfanggerüst'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811', 'DIN 4426'],
    'B 121', 'Dachdecker', 'Dachdecker', 'Dachdecker',
    { merkmale: ['dacharbeiten'] }),
  z(ARBEITSPLAETZE, 'Arbeiten an Bodenöffnungen: Absturz, Hineintreten',
    ['Umwehrung 3-teilig, Bretter min. 3/15 cm', 'Abdeckung nur Bohlen mit Holzklasse S10'],
    ['DGUV Vorschrift 38'],
    'B 100', 'Rohbauer', 'Rohbauer', 'Rohbauer'),
  z(ARBEITSPLAETZE, 'Arbeiten an Außenwandöffnungen: Absturz',
    ['3-teil. Seitenschutz: Bretter min. 3/15 cm', 'Fanggerüst', 'Behelfsgerüst'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811'],
    'B 100', 'Rohbauer', 'Rohbauer', 'Rohbauer'),
  z(ARBEITSPLAETZE, 'Arbeiten an Treppenlauf,-podest: Absturz',
    ['3-teil. Seitenschutz: Bretter min. 3/15 cm', 'Fanggerüst'],
    ['DIN 4420', 'DIN EN 12810/12811', 'DGUV Vorschrift 38'],
    'B 100', 'Rohbauer', 'Rohbauer', 'Rohbauer'),
  z(ARBEITSPLAETZE, 'Arbeiten mit PSAgA / Seilzugangstechnik: Absturz, Hängetrauma',
    ['nur wenn Seitenschutz oder Auffangeinrichtung nicht möglich', 'Anschlagpunkte festlegen, Tragfähigkeit nachweisen', 'Rettung aus dem Hängen in kurzer Zeit sicherstellen', 'Seilzugang: mind. zwei Personen, zweites Sicherungsseil', 'Unterweisung mit praktischer Übung', 'PSAgA vor jeder Benutzung auf Mängel prüfen'],
    ['DGUV Regel 112-198', 'DGUV Regel 112-199', 'DGUV Information 212-001'],
    'n.v.', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(ARBEITSPLAETZE, 'Arbeiten vom Wasser aus, schwimmende Geräte: Ertrinken, Kentern',
    ['Rettungswesten tragen', 'Rettungsboot / Rettungsmittel einsatzbereit halten', 'Schwimmfähigkeit und Kentersicherheit des Geräts nachweisen', 'Geländer und rutschhemmende Verkehrsgänge', 'Sichere Landverbindung', 'Wetterlage und Wasserstand beobachten'],
    ['UVV „Schwimmende Geräte“', 'DGUV Vorschrift 38'],
    'n.v.', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk',
    { merkmale: ['gewaesser'] }),
  z(ARBEITSPLAETZE, 'Arbeiten in Schächten, Kanälen und engen Räumen: Gase, Sauerstoffmangel',
    ['Einstieg nur nach schriftlicher Freigabe', 'Vor und während der Arbeit freimessen, Gaswarngerät', 'Technische Lüftung', 'Sicherungsposten außerhalb mit Verständigung', 'Rettungsausrüstung (Dreibein, Auffanggurt) bereithalten', 'Atemschutz nach Gefährdungsbeurteilung'],
    ['DGUV Regel 103-003', 'DGUV Regel 113-004', 'DGUV Vorschrift 1'],
    'n.v.', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),

  // ── Unterkünfte ───────────────────────────────────────────────────────
  z(UNTERKUENFTE, 'mangelnde Hygienemaßn.: Gesundheitsschäden/Infektionen',
    ['Sanitäranlagen (Reinigung)', 'Tagesunterkünfte'],
    ['ASR A4.1 und ASR A4.3', 'ArbStättV', 'GewerbeOrdnung', 'DGUV Information 212-017', 'DGUV Vorschrift 1'],
    'A 025', 'Bauherr', 'Bauherr', 'Bauherr'),

  // ── Maschinen, Geräte, Gerüste ────────────────────────────────────────
  z(MASCHINEN, 'Prüfungen von Arbeitsmitteln',
    ['Arbeitsmittel regelmäßig prüfen lassen', 'Prüfergebnisse dokumentieren', 'Prüfergebnisse bis zum nächsten Prüftermin aufbewahren'],
    ['DGUV Vorschrift 1', 'TRBS 1201', 'TRBS 1203', 'DGUV Regel 100-500', 'DGUV Information 203-071'],
    'A 007', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(MASCHINEN, 'Fassadengerüst: Absturz, Umsturz, Hineintreten, Anstoßen',
    ['3-teil. Seitenschutz: Oberholm-Mittelh.-Bordbr.', 'Abstand zum Bauwerk max. 30 cm', 'Standsicherheit', 'lückenloser Belag', 'Verkehrsraum freihalten (Gerüsthalter)'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811', 'TRGS 2121 T. 2'],
    'B 113', 'Gerüstbauer', 'jeweiliges Gewerk', 'Gerüstbauer',
    { merkmale: ['geruest'] }),
  z(MASCHINEN, 'Fahrbare Arbeitsbühnen: Absturz, Umsturz',
    ['3-teiliger Seitenschutz', 'Untergrund eben, tragfähig, hindernisfrei', 'Anprall vermeiden', 'kein Aufenthalt beim Verschieben', 'Aufbau- und Verwendungsanleitung beachten'],
    ['DGUV Vorschrift 38', 'DIN EN 1004'],
    'B 112', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(MASCHINEN, 'Dachfanggerüste: Absturz',
    ['Tragfähige Schutznetze', 'Bordwand mind. 0,6 m hoch', 'Netzmaschenweite max. 2 cm'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 12810/12811', 'DIN 4426'],
    'B 121', 'Gerüstbauer', 'Dachdecker', 'Gerüstbauer',
    { merkmale: ['dacharbeiten'] }),
  z(MASCHINEN, 'Baukran, Lastentransport: herabfallende Gegenstände',
    ['Gefahrbereich unter der Last absperren', 'standsicherer Aufbau', 'zul. Höchstlast nicht überschreiten', 'geeignete Lastaufnahmemittel', 'nur einwandfreie und geprüfte Seile, Ketten, Haken etc.', 'Schutzhelme tragen', 'keine Personenbeförderung', 'Verständigung Kran/Anschläger sichern'],
    ['DGUV Vorschrift 1', 'DGUV Vorschrift 52', 'DIN 15001', 'DIN 15003, DIN 15019', 'DIN 15105, DIN 15106'],
    'B 213; B 214', 'Baukran', 'Baukran', 'Baukran',
    { merkmale: ['kran'] }),
  z(MASCHINEN, 'Personenaufnahme am Kran (Arbeitskorb): Absturz, herabfallende Gegenstände',
    ['nur wenn andere Zugangsmittel ungeeignet sind', 'Arbeitskorb und Kran für Personenaufnahme geeignet und geprüft', 'Probefahrt mit Prüflast vor dem Einsatz', 'Kranführer bleibt am Steuerstand, ständige Verständigung', 'PSAgA im Arbeitskorb anschlagen', 'Bereich unter dem Arbeitskorb absperren'],
    ['DGUV Regel 101-005', 'DGUV Vorschrift 52'],
    'n.v.', 'Baukran', 'Baukran', 'Baukran',
    { merkmale: ['kran'] }),
  z(MASCHINEN, 'Mehrere Krane, überschneidende Arbeitsbereiche: Kollision',
    ['Kranstandorte und Schwenkbereiche vorab abstimmen', 'Ausleger in der Höhe staffeln', 'Arbeitsbereichsbegrenzung / Antikollisionssystem', 'Verständigung der Kranführer untereinander regeln', 'Aufsichtführenden für den Kranbetrieb benennen'],
    ['DGUV Vorschrift 52 § 33'],
    'n.v.', 'Bauherr', 'Baukran', 'Baukran',
    { merkmale: ['kran'] }),
  z(MASCHINEN, 'Baustromversorgung: Elektr. Strom',
    ['FI-Schutzeinrichtung', 'tägl./monatl./halbjährl. Prüfungen'],
    ['DGUV Vorschrift 3', 'VDE'],
    'B 171; B 172', 'Auftragnehmer (AN)', 'Elektrofachkraft', 'Elektrofachkraft'),
  z(MASCHINEN, 'Schutzdächer/-netze: herabfallende Gegenstände',
    ['bei übereinanderliegenden Arbeitsplätzen', 'Arbeitsplätze/Verkehrswege schützen', 'öffentl. Verkehr, Passanten schützen'],
    ['DGUV Vorschrift 38', 'DIN 4420', 'DIN EN 18210/18211'],
    'B 114', 'Gerüstbauer', 'jeweiliges Gewerk', 'Gerüstbauer'),

  // ── Arbeitsschutz ─────────────────────────────────────────────────────
  z(ARBEITSSCHUTZ, 'Brandschutzeinrichtung: Ersticken, Verbrennen',
    ['Handfeuerlöscher', 'Löschmittel / Erste-Hilfe'],
    ['ASR A2.2', 'DIN 14096', 'DGUV Vorschrift 1'],
    'A 021', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk',
    { dokumente: [BAUSTELLENORDNUNG] }),
  z(ARBEITSSCHUTZ, 'Erste-Hilfe-Einrichtungen: Fehlende Ersthelfer u. Versorgung',
    ['ausreichend Ersthelfer', 'Erste-Hilfe-Einrichtungen', 'Notruf-Organisation', 'Rettungsgeräte, -transportmittel'],
    ['DGUV Vorschrift 1', 'ArbStättV', 'DGUV Information 212-139', 'DIN 13157', 'DIN 13169'],
    'A 004', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),

  // ── Arbeitsverfahren ──────────────────────────────────────────────────
  z(VERFAHREN, 'Arbeiten über Verkehrswegen: herabfallende Gegenstände',
    ['Koordination (zeitl./räuml. Trennung)', 'Schutzdächer', 'Schutzhelme'],
    ['BaustellV', 'DGUV Vorschrift 1', 'DGUV Vorschrift 38'],
    'E 602', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERFAHREN, 'Arbeiten über Arbeitsplätzen: herabfallende Gegenstände',
    ['Koordination (zeitl./räuml. Trennung)', 'Schutzdächer', 'Schutzhelme'],
    ['BaustellV', 'DGUV Vorschrift 1', 'DGUV Vorschrift 38'],
    'B 114', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERFAHREN, 'Krane, Seilbagger, Lastentransport: Quetsch- u. Scherstellen',
    ['Last nicht über Personen schwenken', 'ausreichende Tragfähigkeit', 'Last gegen Herabfallen sichern', 'Hakensicherung', 'zusätzl. Schutzhelme'],
    ['DGUV Vorschrift 52', 'DIN EN 14439'],
    'B 213; B 214', 'Baukran', 'Baukran', 'Baukran',
    { merkmale: ['kran'] }),
  z(VERFAHREN, 'Arbeiten mit offener Flamme; Heißarbeiten',
    ['Koordination (zeitl./räuml. Trennung)', 'Gasflaschen außerhalb v. Verkehrswegen', 'Schutzbereich beachten', 'Mengenbegrenzung', 'Leckgassicherung', 'ausreichende Belüftung', 'Geschulter Brandschutzhelfer', 'Arbeitsfreigabe', 'Arbeitserlaubnisschein (Work-Permit-System)'],
    ['BaustellV', 'DGUV Vorschrift 38', 'DGUV Vorschrift 79', 'TRG 280', 'TRBS 1201', 'DGUV Regel 100-500'],
    'C 423; C 421', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERFAHREN, 'Schlagbohren, Stemmen, Kernbohrung: Lärm u. Staub',
    ['Koordination (zeitl./räuml. Trennung)', 'persönliche Schutzausrüstung', 'Lüftung', 'Absaugen'],
    ['BaustellV', 'DGUV Vorschrift 1', 'DGUV Regel 100-500', 'DGUV Regel 112-194', 'LärmVibrationsArbSchV', 'GefStoffV'],
    'B 206', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERFAHREN, 'Kernbohrungen an Decken: herabfallende Gegenstände',
    ['Gefahrbereiche absperren u. kennzeichnen'],
    ['DGUV Vorschrift 38'],
    'n.v.', 'Heizung, Lüftung, Sanitär', 'Heizung, Lüftung, Sanitär', 'Heizung, Lüftung, Sanitär'),
  z(VERFAHREN, 'Abbrucharbeiten: herabfallende Gegenstände',
    ['Koordination (zeitl./räuml. Trennung)', 'Gefahrbereiche absperren u. kennzeichnen', 'Abwurfeinrichtung', 'Schutzplanen, -netze', 'Abbruchanweisung', 'persönliche Schutzausrüstung'],
    ['BaustellV', 'DGUV Vorschrift 38', 'DGUV Vorschrift 6', 'DGUV Regel 101-011', 'TRGS 559', 'DIN 4420'],
    'C 301-303', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk',
    { merkmale: ['abbruch'] }),
  z(VERFAHREN, '(Trenn-)Schleifen: Lärm und Staub',
    ['Koordination (zeitl./räuml. Trennung)', 'persönliche Schutzausrüstung', 'Lüftung'],
    ['BaustellV', 'DGUV Vorschrift 1', 'DGUV Regel 100-500', 'DGUV Regel 112-194', 'LärmVibrationsArbSchV', 'GefStoffV', 'DGUV Information 209-003'],
    'B 273; B 274', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(VERFAHREN, 'Schweißen: Brand, Verpuffung, O2-Mangel',
    ['Koordination (zeitl./räuml. Trennung)', 'Gasflaschen außerhalb v. Verkehrswegen', 'Schutzbereich beachten', 'Mengenbegrenzung', 'Leckgassicherung', 'ausreichende Belüftung', 'Geschulter Brandschutzhelfer', 'Arbeitsfreigabe'],
    ['BaustellV', 'DGUV Vorschrift 38', 'DGUV Vorschrift 79', 'TRG 280', 'TRBS 1201'],
    'C 423; C 421', 'Schlosser', 'Schlosser', 'Schlosser'),
  z(VERFAHREN, 'Lackierarbeiten: Lösemittel',
    ['Koordination (zeitl./räuml. Trennung)', 'persönliche Schutzausrüstung', 'Lüftung', 'Sicherheitsdatenblätter und schriftliche Anweisung'],
    ['BaustellV', 'DGUV Regel 100-500', 'DGUV Vorschrift 1', 'GefStoffV'],
    'C 404; C 403', 'Maler', 'Maler', 'Maler'),
  z(VERFAHREN, 'Bodenbelagsarbeiten: Lösemittel',
    ['Koordination (zeitl./räuml. Trennung)', 'persönliche Schutzausrüstung', 'Lüftung', 'Sicherheitsdatenblätter und schriftliche Anweisung'],
    ['BaustellV', 'DGUV Regel 100-500', 'DGUV Vorschrift 1', 'GefStoffV', 'TRGS 610'],
    'C 404; C 403', 'Beschichtung', 'Beschichtung', 'Beschichtung'),
  z(VERFAHREN, 'Klebearbeiten: Kunstharze/Lösemittel',
    ['Koordination (zeitl./räuml. Trennung)', 'persönliche Schutzausrüstung', 'Lüftung', 'Sicherheitsdatenblätter und schriftliche Anweisung'],
    ['BaustellV', 'DGUV Regel 100-500', 'DGUV Regel 109-002', 'GefStoffV', 'TRGS 610'],
    'C 403; C 314', 'Bodenbeschichtung', 'Bodenbeschichtung', 'Bodenbeschichtung'),
  z(VERFAHREN, 'Dämmarbeiten m. künstl. Mineralfasern: Staub',
    ['Koordination (zeitl./räuml. Trennung)', 'persönliche Schutzausrüstung', 'Lüftung'],
    ['BaustellV', 'DGUV Vorschrift 1', 'GefStoffV', 'TRGS 521'],
    'C 319', 'Trockenbauer / Isolierung', 'Trockenbauer / Isolierung', 'Trockenbauer / Isolierung'),
  z(VERFAHREN, 'Estricharbeiten: umherschlagende Schläuche, Stolpergefahr',
    ['Koordination (zeitl./räuml. Trennung)', 'Schläuche nicht auf Verkehrswegen', 'Schläuche befestigen'],
    ['BaustellV', 'DGUV Vorschrift 38'],
    'n.v.', 'Estrichleger', 'Estrichleger', 'Estrichleger'),
  z(VERFAHREN, 'Montage Fertigteile',
    ['Montageanweisung'],
    ['DIN 4420', 'DIN EN 12810/12811', 'DGUV Vorschrift 38', 'DGUV Vorschrift 52', 'DGUV Regel 100-500', 'TRBS 2121, T. 1, 2'],
    'n.v.', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk',
    { dokumente: [MONTAGEANWEISUNG], merkmale: ['fertigteilmontage'] }),
  z(VERFAHREN, 'Arbeiten in der Nähe von Freileitungen: Stromschlag, Lichtbogen',
    ['Freischalten beim Netzbetreiber anfordern', 'Schutzabstände je Nennspannung einhalten', 'Höhenbegrenzung / Abschrankung an Durchfahrten', 'Kran- und Baggereinsatz nur unter Aufsicht', 'Unterweisung zum Verhalten bei Leitungsberührung'],
    ['DGUV Vorschrift 3', 'DGUV Vorschrift 38 § 6', 'DIN VDE 0105-100'],
    'n.v.', 'Bauherr', 'jeweiliges Gewerk', 'jeweiliger Versorger',
    { merkmale: ['freileitung'] }),
  z(VERFAHREN, 'Spezialtiefbau (Bohrpfähle, Anker, Rammen): Umsturz, Erschütterung',
    ['Tragfähige Arbeitsebene für Bohr- und Rammgeräte herstellen', 'Gefahrbereich um Mäkler und Bohrgerät absperren', 'Erschütterungen an Nachbarbebauung überwachen', 'Bohrlöcher sichern oder abdecken', 'Hydraulikschläuche geschützt verlegen', 'Verständigung Geräteführer / Helfer sichern'],
    ['DGUV Regel „Arbeiten im Spezialtiefbau“', 'DGUV Vorschrift 38', 'DIN EN 16228'],
    'n.v.', 'Tiefbauer', 'Tiefbauer', 'Tiefbauer',
    { merkmale: ['erdarbeiten', 'tunnelbau'] }),

  // ── Besondere Arbeitsbedingungen ──────────────────────────────────────
  z(BESONDERS, 'Lärm',
    ['Gehörschutz', 'lärmarme Geräte', 'Tages-Lärmexpositionspegel einhalten', 'Kapselung der Lärmquelle/Lärmschutzwände', 'Informationen aus Gefährdungsbeurteilung des AN beachten'],
    ['DGUV Regel 112-194', 'DGUV Information 212-024', 'Lärmschutzverordnung', 'DGUV Vorschrift 6'],
    'A 030', 'jeweiliges Gewerk', 'jeweiliges Gewerk', 'jeweiliges Gewerk'),
  z(BESONDERS, 'Hitze: Hitzekollaps, Hitzschlag, Kreislauf, Dehydration',
    ['Ausreichende Flüssigkeitszufuhr', 'Ausreichend Pausen', 'Mitarbeiterrotation', 'Informationen aus Gefährdungsbeurteilung des AN beachten'],
    ['DGUV Information 213-002', 'DGUV Vorschrift 1', 'DGUV Regel 100-001'],
    'D 505', 'alle Gewerke', 'alle Gewerke', 'alle Gewerke'),
  z(BESONDERS, 'Gefährdung durch Biostoffe: Viren und/oder Erreger',
    ['Hygienemaßnahmen verstärken', 'Leicht zu reinigende Oberflächen bzw. Arbeitsmittel', 'Auswahl der Arbeitsverfahren (Reduktion von Überträgern)', 'Koordination (zeitl./räuml. Trennung)', 'Betriebsanweisung', 'PSA'],
    ['TRBA 200', 'TRBA 400', 'TRBA 500', 'ASR A4.1', 'Biostoffverordnung', 'DGUV Vorschrift 1'],
    'D 503', 'alle Gewerke, Dritte', 'alle Gewerke, Dritte', 'alle Gewerke, Dritte'),
  z(BESONDERS, 'Arbeiten in kontaminierten Bereichen: Gefahrstoffe, Biostoffe',
    ['Schwarz-Weiß-Bereich mit Hygieneeinrichtung', 'Staubarme Verfahren, Befeuchten', 'Atemschutz und Schutzkleidung nach Gefährdungsbeurteilung', 'Luftmessungen während der Arbeiten', 'Zutritt nur für unterwiesene Beschäftigte', 'Arbeitsmedizinische Vorsorge'],
    ['DGUV Regel 101-004', 'GefStoffV', 'TRGS 524', 'Biostoffverordnung'],
    'n.v.', 'Bauherr', 'Auftragnehmer (AN)', 'Auftragnehmer (AN)',
    // Beide Verdachtswege (Entscheid E4): Boden/Altlasten und Gebäudeschadstoffe.
    { merkmale: ['altlastenverdacht', 'gefahrstoffeAnhang2', 'bestand', 'abbruch'] }),
];

// Typische Bauphasen je Hauptabschnitt als Anteil der Projektlaufzeit [start, ende] (0..1).
// Beim Muster-Import in echte Daten umgerechnet (Projekt-Start/Ende).
export const ABSCHNITT_PHASEN: Record<string, [number, number]> = {
  'Baustellenvorbereitung': [0.0, 0.1],
  'Baustelleneinrichtung': [0.05, 0.2],
  'gemeinsam genutzte Einrichtung: VERKEHRSWEGE': [0.05, 0.95],
  'gemeinsam genutzte Einrichtung: LAGERPLÄTZE': [0.1, 0.9],
  'gemeinsam genutzte Einrichtung: ARBEITSPLÄTZE': [0.15, 0.85],
  'gemeinsam genutzte Einrichtung: UNTERKÜNFTE': [0.05, 0.95],
  'gemeinsam genutzte Einrichtung: MASCHINEN, GERÄTE, GERÜSTE': [0.1, 0.8],
  'gemeinsam genutzte Einrichtung: ARBEITSSCHUTZ': [0.0, 1.0],
  'ARBEITSVERFAHREN mit Gefährdung anderer Gewerke': [0.2, 0.9],
  'Besondere Arbeitsbedingungen: Lärm, Hitze, Biostoffe': [0.0, 1.0],
};

// Standard-Legende gemäß Muster
export const MUSTER_LEGENDE: { kuerzel: string; bedeutung: string }[] = [
  { kuerzel: 'ArbStättV', bedeutung: 'Arbeitsstättenverordnung' },
  { kuerzel: 'ASR', bedeutung: 'Arbeitsstättenrichtlinie' },
  { kuerzel: 'BaustellenO', bedeutung: 'Baustellenordnung' },
  { kuerzel: 'LBO', bedeutung: 'Landesbauordnung' },
  { kuerzel: 'BaustellV', bedeutung: 'Baustellenverordnung' },
  { kuerzel: 'BetrSichV', bedeutung: 'Betriebssicherheitsverordnung' },
  { kuerzel: 'DIN', bedeutung: 'Deutsche Industrie Norm' },
  { kuerzel: 'EVU', bedeutung: 'Energie-Versorgungs-Unternehmen' },
  { kuerzel: 'GefStoffV', bedeutung: 'Verordnung zum Schutz vor gefährlichen Stoffen' },
  { kuerzel: 'GewO', bedeutung: 'Gewerbeordnung' },
  { kuerzel: 'RL', bedeutung: 'Richtlinie' },
  { kuerzel: 'RSA', bedeutung: 'Richtlinien für die Sicherung von Arbeitsstellen an Straßen' },
  { kuerzel: 'StVO', bedeutung: 'Straßenverkehrsordnung' },
  { kuerzel: 'TRG', bedeutung: 'Technische Regeln für Druckgase' },
  { kuerzel: 'TRGS', bedeutung: 'Technische Regeln Gefahrstoffe' },
  { kuerzel: 'UVV / BGV', bedeutung: 'Unfallverhütungsvorschriften (Vorschriften der Berufsgenossenschaften)' },
];

// LMRA-Texte gemäß Muster (zwei Boxen)
export const MUSTER_LMRA_FAKTOREN = [
  'Kontakt: Quetschen, Stoßen, Scherstellen, Schnittverletzungen | Gefährliche Oberflächen | Pendelnde, schwebende Lasten',
  'Stürze: aus der Höhe | auf der Ebene (Stolperstellen, Rutschgefahr) | Treppen, Leitern',
  'Umwelteinflüsse: Wind, Sturm | Gewitter | Lärm/Strahlung | Sichtbehinderung',
  'Eingeschlossen: in | unter | zwischen | von',
  'Energiequellen: Druck | Elektrizität | mechanische Kräfte | heiße, kalte Flächen',
  'Überanstrengung: heben | tragen | biegen | drücken/ziehen',
  'Gefahrstoffe: Gase, Dämpfe, Stäube | Flüssigkeiten',
  'Brandgefahren: Brennbares Material | Zündquellen',
];

export const MUSTER_LMRA_REGELN = [
  'Bewerte die Gefahr: Was könnte schlimmstenfalls passieren? Ist mir meine Aufgabe klar? Wird meine Arbeit von Anderen beeinflusst (oben, unten, neben)?',
  'Überlege, wie du die Gefahren vermindern kannst: Richtige Ausbildung und Wissen? Passende Werkzeuge (geprüft und i.O.)? Geeignete PSA? Arbeitsplatz sicher (aufgeräumt, Fluchtwege frei)? Nächstgelegene Noteinrichtungen bekannt?',
  'Tue das Richtige: Beachte die schriftlichen Anweisungen. Halte ich die geplante Ausführung für sicher? Frage nach, wenn Du alleine nicht weiterkommst oder Dir unsicher bist.',
  'Entweder arbeite ich SICHER, oder gar nicht!',
];
